/**
 * Surveillance Detection Service
 * 
 * Browser-level detection of surveillance attempts, API tampering,
 * and suspicious network behavior for P2P UDP WebRTC streams.
 * Designed to run analysis in a dedicated Web Worker while browser API
 * hooks remain on the main thread.
 * 
 * DETECTION LAYERS:
 * 1. Browser APIs     — Tampering detection (RTCPeerConnection, fetch, WebSocket)
 * 2. Network Timing   — MITM proxy detection via latency analysis
 * 3. WebRTC Integrity — Unauthorized ICE/TURN, candidate-pair rerouting
 * 4. DTLS Fingerprint — Certificate mismatch on the UDP/DTLS path  [P0]
 * 5. Peer Identity    — Public-key ↔ DTLS fingerprint binding       [P0]
 * 6. Candidate Pair   — Selected pair monitoring & mid-session change [P1]
 * 7. Mesh Topology    — Cross-peer IP consistency for group calls    [P1]
 * 8. SDP Integrity    — Codec downgrade, ext injection, extra candidates [P2]
 * 9. Throughput        — DataChannel anomaly vs expected transfer rate [P2]
 * 10. Privacy          — Clipboard/screen-capture frequency
 * 
 * INTEGRATION:
 * - NOT currently wired into the app — exists for planned UX integration
 * - Analysis logic is worker-ready: see surveillance-detection-worker.ts
 * - Follows Ngentix metadata-only principle — no message/file content is processed
 * - Emits metadata-only SurveillanceAlert objects via EventEmitter
 * 
 * FUTURE: Daemon integration for OS-level detection (Graphite, Pegasus indicators)
 */

// ─── Types (shared with worker) ─────────────────────────────────────────────

export enum ThreatLevel {
  INFO = 'info',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum ThreatType {
  // Layer 1: API Tampering
  API_TAMPERING = 'api_tampering',
  WEBRTC_MODIFIED = 'webrtc_modified',
  FETCH_MODIFIED = 'fetch_modified',
  WEBSOCKET_MODIFIED = 'websocket_modified',
  
  // Layer 2: Network Anomalies
  TIMING_ANOMALY = 'timing_anomaly',
  UNAUTHORIZED_TURN = 'unauthorized_turn',
  PEER_IP_COLLISION = 'peer_ip_collision',
  UNSTABLE_CONNECTION = 'unstable_connection',
  
  // Layer 3–4: DTLS / Peer Identity  [P0]
  DTLS_FINGERPRINT_MISMATCH = 'dtls_fingerprint_mismatch',
  PEER_IDENTITY_MISMATCH = 'peer_identity_mismatch',
  
  // Layer 6: Candidate Pair  [P1]
  CANDIDATE_PAIR_REROUTED = 'candidate_pair_rerouted',
  UNEXPECTED_RELAY = 'unexpected_relay',
  
  // Layer 7: Mesh Topology  [P1]
  MESH_TOPOLOGY_INCONSISTENCY = 'mesh_topology_inconsistency',
  
  // Layer 8: SDP Integrity  [P2]
  SDP_CODEC_DOWNGRADE = 'sdp_codec_downgrade',
  SDP_EXTENSION_INJECTION = 'sdp_extension_injection',
  SDP_CANDIDATE_INJECTION = 'sdp_candidate_injection',
  
  // Layer 9: Throughput  [P2]
  THROUGHPUT_ANOMALY = 'throughput_anomaly',
  
  // Layer 10: Privacy
  EXCESSIVE_CLIPBOARD_ACCESS = 'excessive_clipboard_access',
  SCREEN_CAPTURE_REQUEST = 'screen_capture_request',
  SUSPICIOUS_PERMISSIONS = 'suspicious_permissions',
  
  // Extension Threats
  MALICIOUS_EXTENSION = 'malicious_extension',
  PERMISSION_ESCALATION = 'permission_escalation',
  
  // Future: OS-Level (via daemon)
  KERNEL_HOOK_DETECTED = 'kernel_hook_detected',
  GRAPHITE_SIGNATURE = 'graphite_signature',
  PEGASUS_INDICATOR = 'pegasus_indicator',
  NETWORK_MONITOR = 'network_monitor'
}

export interface SurveillanceAlert {
  id: string;
  timestamp: number;
  level: ThreatLevel;
  type: ThreatType;
  title: string;
  details: string;
  recommendation: string;
  technicalData?: Record<string, any>;
  dismissed?: boolean;
}

export interface NetworkTimingStats {
  measurements: number[];
  avg: number;
  stdDev: number;
  variance: number;
  suspicious: boolean;
}

/** DTLS fingerprint extracted from SDP answer/offer */
export interface DTLSFingerprintRecord {
  peerId: string;
  sdpFingerprint: string;   // a=fingerprint:sha-256 XX:XX:...
  liveFingerprint?: string; // from RTCDtlsTransport.getRemoteCertificates()
  verified: boolean;
  lastChecked: number;
}

/** Peer identity binding: Moby address ↔ DTLS certificate */
export interface PeerIdentityBinding {
  peerAddress: string;       // Moby public-key address
  expectedFingerprint: string; // signed during signaling exchange
  actualFingerprint?: string;
  verified: boolean;
  lastVerified: number;
}

/** Snapshot of a selected ICE candidate pair for change detection */
export interface CandidatePairSnapshot {
  peerId: string;
  localType: string;   // host | srflx | relay
  remoteType: string;
  localIP: string;
  remoteIP: string;
  timestamp: number;
}

/** Per-peer throughput tracking for anomaly detection */
export interface ThroughputSample {
  peerId: string;
  bytesSent: number;
  bytesReceived: number;
  timestamp: number;
}

// ─── Worker message types ───────────────────────────────────────────────────

export type SurveillanceWorkerInbound =
  | { type: 'analyze-timing'; measurements: number[] }
  | { type: 'analyze-sdp'; peerId: string; sdp: string; direction: 'offer' | 'answer' }
  | { type: 'analyze-throughput'; peerId: string; samples: ThroughputSample[]; expectedBandwidth: number }
  | { type: 'analyze-mesh-topology'; meshReport: MeshTopologyReport }
  | { type: 'set-config'; config: Partial<SurveillanceConfig> };

export type SurveillanceWorkerOutbound =
  | { type: 'alert'; alert: Omit<SurveillanceAlert, 'id' | 'timestamp'> }
  | { type: 'timing-result'; stats: NetworkTimingStats }
  | { type: 'sdp-result'; peerId: string; issues: SDPIssue[] }
  | { type: 'throughput-result'; peerId: string; anomaly: boolean; details: string }
  | { type: 'mesh-result'; inconsistencies: MeshInconsistency[] };

export interface MeshTopologyReport {
  sessionId: string;
  selfAddress: string;
  /** Each peer's view: peerId → Set<IPs they resolved to> */
  peerIPMap: Record<string, string[]>;
}

export interface MeshInconsistency {
  targetPeer: string;
  reportingPeers: string[];
  ipsReported: string[][];
  details: string;
}

export interface SDPIssue {
  type: ThreatType;
  details: string;
  technicalData: Record<string, any>;
}

export interface SurveillanceConfig {
  authorizedServers: string[];
  timingAnomalyThreshold: number;     // stdDev/avg ratio (default 0.5)
  reconnectAlertThreshold: number;    // consecutive drops before alert (default 3)
  clipboardRateLimit: number;         // max reads per minute (default 10)
  throughputDropThreshold: number;    // % drop from expected (default 0.6 = 60% drop)
  integrityCheckIntervalMs: number;   // API integrity poll (default 30000)
  candidatePairPollIntervalMs: number; // selected pair poll (default 10000)
}

const DEFAULT_CONFIG: SurveillanceConfig = {
  authorizedServers: [
    'stun.l.google.com',
    'global.stun.twilio.com',
    'turn:relay1.moby.chat',
    'turn:relay2.moby.chat',
    'turn:relay3.moby.chat',
  ],
  timingAnomalyThreshold: 0.5,
  reconnectAlertThreshold: 3,
  clipboardRateLimit: 10,
  throughputDropThreshold: 0.6,
  integrityCheckIntervalMs: 30_000,
  candidatePairPollIntervalMs: 10_000,
};

// ─── Internal types ─────────────────────────────────────────────────────────

interface APISnapshot {
  RTCPeerConnection: typeof RTCPeerConnection | undefined;
  fetch: typeof fetch | undefined;
  WebSocket: typeof WebSocket | undefined;
  navigator_clipboard_readText: (() => Promise<string>) | undefined;
  navigator_mediaDevices_getDisplayMedia: ((constraints?: DisplayMediaStreamOptions) => Promise<MediaStream>) | undefined;
}

interface MonitoredPeer {
  connection: RTCPeerConnection;
  peerId: string;
  iceIPs: Set<string>;
  reconnectCount: number;
  lastCandidatePair: CandidatePairSnapshot | null;
  dtlsFingerprint: DTLSFingerprintRecord | null;
  identityBinding: PeerIdentityBinding | null;
  throughputHistory: ThroughputSample[];
  candidatePairPollTimer?: ReturnType<typeof setInterval>;
}

// ─── IPv4 + IPv6 extraction ─────────────────────────────────────────────────

const IP_V4_REGEX = /(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/;
const IP_V6_REGEX = /([0-9a-fA-F]{1,4}(?::[0-9a-fA-F]{1,4}){7}|(?:[0-9a-fA-F]{1,4}:){1,7}:|::(?:[0-9a-fA-F]{1,4}:){0,5}[0-9a-fA-F]{1,4})/;

function extractIP(candidate: string): string | null {
  const v4 = candidate.match(IP_V4_REGEX);
  if (v4) return v4[1];
  const v6 = candidate.match(IP_V6_REGEX);
  if (v6) return v6[1];
  return null;
}

// ─── SDP analysis (pure functions — worker-safe) ────────────────────────────

const PREFERRED_AUDIO_CODECS = ['opus'];
const PREFERRED_VIDEO_CODECS = ['VP8', 'VP9', 'H264', 'AV1'];

export function analyzeSDPContent(peerId: string, sdp: string, direction: 'offer' | 'answer'): SDPIssue[] {
  const issues: SDPIssue[] = [];
  const lines = sdp.split('\r\n');

  // Check for unexpected a=extmap lines (extension header injection)
  const extmaps = lines.filter(l => l.startsWith('a=extmap:'));
  const knownExtensions = [
    'urn:ietf:params:rtp-hdrext:ssrc-audio-level',
    'urn:ietf:params:rtp-hdrext:toffset',
    'http://www.webrtc.org/experiments/rtp-hdrext/abs-send-time',
    'urn:3gpp:video-orientation',
    'http://www.ietf.org/id/draft-holmer-rmcat-transport-wide-cc-extensions-01',
    'urn:ietf:params:rtp-hdrext:sdes:mid',
    'urn:ietf:params:rtp-hdrext:sdes:rtp-stream-id',
    'urn:ietf:params:rtp-hdrext:sdes:repaired-rtp-stream-id',
  ];
  for (const ext of extmaps) {
    const uri = ext.split(' ').slice(1).join(' ');
    const isKnown = knownExtensions.some(known => uri.includes(known));
    if (!isKnown) {
      issues.push({
        type: ThreatType.SDP_EXTENSION_INJECTION,
        details: `Unknown RTP header extension in ${direction}: ${uri.slice(0, 80)}`,
        technicalData: { peerId: peerId.slice(0, 8), direction, extension: uri },
      });
    }
  }

  // Check for codec downgrade (e.g., forcing unencrypted or weak codec)
  const rtpmapLines = lines.filter(l => l.startsWith('a=rtpmap:'));
  const codecs = rtpmapLines.map(l => {
    const parts = l.split(' ');
    return parts[1]?.split('/')[0] || '';
  }).filter(Boolean);

  if (codecs.length > 0) {
    const hasPreferredAudio = codecs.some(c => PREFERRED_AUDIO_CODECS.includes(c.toLowerCase()));
    const hasPreferredVideo = codecs.some(c => PREFERRED_VIDEO_CODECS.includes(c));
    const hasAudioMedia = lines.some(l => l.startsWith('m=audio'));
    const hasVideoMedia = lines.some(l => l.startsWith('m=video'));

    if (hasAudioMedia && !hasPreferredAudio) {
      issues.push({
        type: ThreatType.SDP_CODEC_DOWNGRADE,
        details: `Audio ${direction} missing preferred codecs (opus). Available: ${codecs.join(', ')}`,
        technicalData: { peerId: peerId.slice(0, 8), direction, codecs },
      });
    }
    if (hasVideoMedia && !hasPreferredVideo) {
      issues.push({
        type: ThreatType.SDP_CODEC_DOWNGRADE,
        details: `Video ${direction} missing preferred codecs (VP8/VP9/H264/AV1). Available: ${codecs.join(', ')}`,
        technicalData: { peerId: peerId.slice(0, 8), direction, codecs },
      });
    }
  }

  // Check for candidate lines injected into the SDP body (unusual — candidates
  // normally arrive via trickle ICE, not baked into the SDP)
  const sdpCandidates = lines.filter(l => l.startsWith('a=candidate:'));
  if (sdpCandidates.length > 10) {
    issues.push({
      type: ThreatType.SDP_CANDIDATE_INJECTION,
      details: `Unusually high candidate count (${sdpCandidates.length}) baked into SDP ${direction}`,
      technicalData: { peerId: peerId.slice(0, 8), direction, count: sdpCandidates.length },
    });
  }

  return issues;
}

/** Extract the DTLS fingerprint from an SDP string */
export function extractSDPFingerprint(sdp: string): string | null {
  const match = sdp.match(/a=fingerprint:(sha-256\s+[A-Fa-f0-9:]+)/);
  return match ? match[1] : null;
}

// ─── Throughput analysis (pure function — worker-safe) ──────────────────────

export function analyzeThroughput(
  samples: ThroughputSample[],
  expectedBandwidth: number,
  dropThreshold: number
): { anomaly: boolean; details: string; avgRate: number } {
  if (samples.length < 2) {
    return { anomaly: false, details: 'Insufficient samples', avgRate: 0 };
  }

  // Calculate average throughput over the sample window
  const sorted = [...samples].sort((a, b) => a.timestamp - b.timestamp);
  const totalBytes = sorted[sorted.length - 1].bytesReceived - sorted[0].bytesReceived;
  const durationMs = sorted[sorted.length - 1].timestamp - sorted[0].timestamp;
  if (durationMs <= 0) {
    return { anomaly: false, details: 'Zero duration window', avgRate: 0 };
  }

  const avgRate = (totalBytes / durationMs) * 1000; // bytes/sec
  const ratio = avgRate / expectedBandwidth;

  if (ratio < (1 - dropThreshold)) {
    return {
      anomaly: true,
      details: `Throughput ${(avgRate / 1024).toFixed(1)} KB/s is ${((1 - ratio) * 100).toFixed(0)}% below expected ${(expectedBandwidth / 1024).toFixed(1)} KB/s`,
      avgRate,
    };
  }

  return { anomaly: false, details: 'Within expected range', avgRate };
}

// ─── Mesh topology analysis (pure function — worker-safe) ───────────────────

export function analyzeMeshTopology(report: MeshTopologyReport): MeshInconsistency[] {
  const inconsistencies: MeshInconsistency[] = [];
  const peerIds = Object.keys(report.peerIPMap);

  // For each peer, check if all other peers agree on their IP
  for (const targetPeer of peerIds) {
    const targetIPs = new Set(report.peerIPMap[targetPeer] || []);
    if (targetIPs.size === 0) continue;

    // Compare with every other peer's view
    for (const otherPeer of peerIds) {
      if (otherPeer === targetPeer) continue;
      const otherIPs = new Set(report.peerIPMap[otherPeer] || []);

      // If both peers have IPs and they don't overlap at all, that's suspicious
      // (In a normal mesh, peer A's remote candidate for peer B should match
      //  peer C's remote candidate for peer B)
      // This is a cross-check only possible in group/mesh calls
    }
  }

  // Simplified: check if any peer resolves to IPs that no other connection sees
  // This catches a targeted interception on one link in the mesh
  const allIPsByPeer = new Map<string, Set<string>>();
  for (const [peer, ips] of Object.entries(report.peerIPMap)) {
    allIPsByPeer.set(peer, new Set(ips));
  }

  for (const [peerA, ipsA] of allIPsByPeer) {
    for (const [peerB, ipsB] of allIPsByPeer) {
      if (peerA >= peerB) continue; // avoid duplicate pairs
      
      // If peer A and peer B share an IP but they're different logical peers,
      // one of them might be a MITM node impersonating the other
      const sharedIPs = [...ipsA].filter(ip => ipsB.has(ip));
      if (sharedIPs.length > 0 && peerA !== report.selfAddress && peerB !== report.selfAddress) {
        inconsistencies.push({
          targetPeer: peerA,
          reportingPeers: [peerB],
          ipsReported: [[...ipsA], [...ipsB]],
          details: `Peers ${peerA.slice(0, 8)} and ${peerB.slice(0, 8)} share IP(s): ${sharedIPs.join(', ')} — possible MITM relay`,
        });
      }
    }
  }

  return inconsistencies;
}

// ─── Timing analysis (pure function — worker-safe) ──────────────────────────

export function analyzeTimingMeasurements(
  measurements: number[],
  threshold: number
): NetworkTimingStats {
  const avg = measurements.reduce((a, b) => a + b, 0) / measurements.length;
  const variance = measurements.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / measurements.length;
  const stdDev = Math.sqrt(variance);
  const suspicious = stdDev > avg * threshold;

  return { measurements, avg, stdDev, variance, suspicious };
}


// ═══════════════════════════════════════════════════════════════════════════
// Main-thread service (browser API hooks + worker coordination)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SurveillanceDetectionService — main-thread coordinator.
 * 
 * Hooks into browser APIs that can only run on the main thread
 * (RTCPeerConnection, fetch, WebSocket, clipboard, getDisplayMedia)
 * and delegates CPU-heavy analysis to a Web Worker.
 * 
 * Emits: 'alert', 'alert-dismissed', 'alerts-cleared'
 */
export class SurveillanceDetectionService {
  private config: SurveillanceConfig;
  private originalAPIs: APISnapshot | null = null;
  private alerts: Map<string, SurveillanceAlert> = new Map();
  private monitoredPeers: Map<string, MonitoredPeer> = new Map();
  private clipboardAccessCount = 0;
  private lastClipboardCheck = 0;
  private isMonitoring = false;
  private integrityCheckTimer?: ReturnType<typeof setInterval>;
  private worker: Worker | null = null;

  // Event listener tracking (simple pub/sub — avoids EventEmitter import)
  private listeners: Map<string, Set<(...args: any[]) => void>> = new Map();

  // Alert deduplication: type+peerId → last alert timestamp
  private recentAlertKeys: Map<string, number> = new Map();
  private static DEDUP_WINDOW_MS = 60_000; // suppress duplicate alerts within 1 min

  constructor(config?: Partial<SurveillanceConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // ─── Pub/Sub ────────────────────────────────────────────────────────────

  on(event: string, listener: (...args: any[]) => void): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(listener);
  }

  off(event: string, listener: (...args: any[]) => void): void {
    this.listeners.get(event)?.delete(listener);
  }

  private emit(event: string, ...args: any[]): void {
    for (const listener of this.listeners.get(event) || []) {
      try { listener(...args); } catch (e) { console.error('[SURVEILLANCE] Listener error:', e); }
    }
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────

  /**
   * Start surveillance monitoring.
   * Call once after page load. Safe to call in SSR (no-ops if window is undefined).
   */
  startMonitoring(): void {
    if (this.isMonitoring) return;
    if (typeof window === 'undefined') return; // SSR guard

    console.log('[SURVEILLANCE] Starting surveillance detection');
    this.isMonitoring = true;

    // Snapshot original APIs before any tampering
    this.originalAPIs = {
      RTCPeerConnection: typeof RTCPeerConnection !== 'undefined' ? RTCPeerConnection : undefined,
      fetch: typeof fetch !== 'undefined' ? fetch : undefined,
      WebSocket: typeof WebSocket !== 'undefined' ? WebSocket : undefined,
      navigator_clipboard_readText: navigator?.clipboard?.readText?.bind(navigator.clipboard),
      navigator_mediaDevices_getDisplayMedia: navigator?.mediaDevices?.getDisplayMedia?.bind(navigator.mediaDevices),
    };

    this.installAPIMonitors();
    this.startWorker();

    // Periodic API integrity checks
    this.integrityCheckTimer = setInterval(
      () => this.performIntegrityCheck(),
      this.config.integrityCheckIntervalMs
    );
    this.performIntegrityCheck();
  }

  /**
   * Stop all monitoring and clean up timers, worker, and per-peer monitors.
   */
  stopMonitoring(): void {
    if (!this.isMonitoring) return;
    console.log('[SURVEILLANCE] Stopping surveillance detection');
    this.isMonitoring = false;

    if (this.integrityCheckTimer) {
      clearInterval(this.integrityCheckTimer);
      this.integrityCheckTimer = undefined;
    }

    // Stop per-peer candidate pair polling
    for (const peer of this.monitoredPeers.values()) {
      if (peer.candidatePairPollTimer) clearInterval(peer.candidatePairPollTimer);
    }
    this.monitoredPeers.clear();

    this.terminateWorker();
  }

  // ─── Worker coordination ────────────────────────────────────────────────

  private startWorker(): void {
    try {
      this.worker = new Worker(
        new URL('../workers/surveillance-detection-worker.ts', import.meta.url),
        { type: 'module' }
      );
      this.worker.onmessage = (e: MessageEvent<SurveillanceWorkerOutbound>) => {
        this.handleWorkerMessage(e.data);
      };
      this.worker.onerror = (e) => {
        console.error('[SURVEILLANCE] Worker error:', e);
      };
      // Send initial config
      this.postToWorker({ type: 'set-config', config: this.config });
    } catch (err) {
      // Worker creation may fail in test/SSR — service continues without offloading
      console.warn('[SURVEILLANCE] Worker unavailable, running analysis on main thread:', err);
      this.worker = null;
    }
  }

  private terminateWorker(): void {
    this.worker?.terminate();
    this.worker = null;
  }

  private postToWorker(msg: SurveillanceWorkerInbound): void {
    this.worker?.postMessage(msg);
  }

  private handleWorkerMessage(msg: SurveillanceWorkerOutbound): void {
    switch (msg.type) {
      case 'alert':
        this.createAlert(msg.alert);
        break;
      case 'timing-result':
        this.emit('timing-analysis', msg.stats);
        break;
      case 'sdp-result':
        for (const issue of msg.issues) {
          this.createAlert({
            level: issue.type === ThreatType.SDP_CODEC_DOWNGRADE ? ThreatLevel.HIGH : ThreatLevel.MEDIUM,
            type: issue.type,
            title: sdpIssueTitleMap[issue.type] || 'SDP Integrity Issue',
            details: issue.details,
            recommendation: 'Review your connection. SDP may have been tampered with by an intermediary.',
            technicalData: issue.technicalData,
          });
        }
        break;
      case 'throughput-result':
        if (msg.anomaly) {
          this.createAlert({
            level: ThreatLevel.MEDIUM,
            type: ThreatType.THROUGHPUT_ANOMALY,
            title: 'DataChannel Throughput Anomaly',
            details: msg.details,
            recommendation: 'Transfer throughput dropped significantly. Possible throttling or interception on the UDP path.',
            technicalData: { peerId: msg.peerId.slice(0, 8) },
          });
        }
        break;
      case 'mesh-result':
        for (const inc of msg.inconsistencies) {
          this.createAlert({
            level: ThreatLevel.HIGH,
            type: ThreatType.MESH_TOPOLOGY_INCONSISTENCY,
            title: 'Group Mesh Topology Inconsistency',
            details: inc.details,
            recommendation: 'A link in the group call mesh may be intercepted. Verify peer identities.',
            technicalData: { targetPeer: inc.targetPeer.slice(0, 8), ips: inc.ipsReported },
          });
        }
        break;
    }
  }

  // ─── Layer 1: API Integrity ─────────────────────────────────────────────

  private installAPIMonitors(): void {
    // Monitor clipboard access
    if (navigator?.clipboard?.readText) {
      const originalReadText = navigator.clipboard.readText.bind(navigator.clipboard);
      const self = this;

      navigator.clipboard.readText = async function () {
        self.clipboardAccessCount++;
        const now = Date.now();

        if (now - self.lastClipboardCheck < 60_000) {
          if (self.clipboardAccessCount > self.config.clipboardRateLimit) {
            self.createAlert({
              level: ThreatLevel.HIGH,
              type: ThreatType.EXCESSIVE_CLIPBOARD_ACCESS,
              title: 'Excessive Clipboard Monitoring',
              details: `Clipboard accessed ${self.clipboardAccessCount} times in 1 minute`,
              recommendation: 'A script or extension may be monitoring your clipboard. Review installed extensions.',
              technicalData: { accessCount: self.clipboardAccessCount, timeWindow: '60s' },
            });
          }
        } else {
          self.clipboardAccessCount = 0;
          self.lastClipboardCheck = now;
        }

        return originalReadText();
      };
    }

    // Monitor screen capture requests
    if (navigator?.mediaDevices?.getDisplayMedia) {
      const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
      const self = this;

      navigator.mediaDevices.getDisplayMedia = async function (constraints?: DisplayMediaStreamOptions) {
        self.createAlert({
          level: ThreatLevel.MEDIUM,
          type: ThreatType.SCREEN_CAPTURE_REQUEST,
          title: 'Screen Capture Requested',
          details: 'Screen sharing was requested by the page',
          recommendation: 'Verify you initiated this action. Malware may attempt unauthorized screen capture.',
          technicalData: { constraints },
        });
        return originalGetDisplayMedia(constraints);
      };
    }
  }

  private performIntegrityCheck(): void {
    if (!this.originalAPIs) return;

    const checks: { api: string; original: any; current: any; level: ThreatLevel; type: ThreatType }[] = [
      {
        api: 'RTCPeerConnection',
        original: this.originalAPIs.RTCPeerConnection,
        current: typeof RTCPeerConnection !== 'undefined' ? RTCPeerConnection : undefined,
        level: ThreatLevel.CRITICAL,
        type: ThreatType.WEBRTC_MODIFIED,
      },
      {
        api: 'fetch',
        original: this.originalAPIs.fetch,
        current: typeof fetch !== 'undefined' ? fetch : undefined,
        level: ThreatLevel.HIGH,
        type: ThreatType.FETCH_MODIFIED,
      },
      {
        api: 'WebSocket',
        original: this.originalAPIs.WebSocket,
        current: typeof WebSocket !== 'undefined' ? WebSocket : undefined,
        level: ThreatLevel.HIGH,
        type: ThreatType.WEBSOCKET_MODIFIED,
      },
    ];

    for (const check of checks) {
      if (check.original && check.current !== check.original) {
        this.createAlert({
          level: check.level,
          type: check.type,
          title: `${check.api} API Modified`,
          details: `${check.api} has been modified after page load`,
          recommendation: `A script or extension may be intercepting ${check.api}. Check installed extensions.`,
          technicalData: { apiName: check.api, tampered: true },
        });
      }
    }
  }

  // ─── Layer 3: WebRTC Peer Monitoring ────────────────────────────────────

  /**
   * Register a peer connection for surveillance monitoring.
   * Call once per RTCPeerConnection after creation.
   *
   * @param peerConnection - The RTCPeerConnection to monitor
   * @param peerId         - Moby peer address (public key)
   * @param expectedPeers  - All expected peer addresses in this session/group
   * @param sdpFingerprint - (optional) DTLS fingerprint from remote SDP for verification
   * @param identityBinding - (optional) Peer identity binding for public-key ↔ cert verification
   */
  monitorPeerConnection(
    peerConnection: RTCPeerConnection,
    peerId: string,
    expectedPeers: string[],
    sdpFingerprint?: string,
    identityBinding?: PeerIdentityBinding,
  ): void {
    // Clean up previous monitor for this peer (prevents unbounded growth)
    this.unmonitorPeer(peerId);

    const monitor: MonitoredPeer = {
      connection: peerConnection,
      peerId,
      iceIPs: new Set(),
      reconnectCount: 0,
      lastCandidatePair: null,
      dtlsFingerprint: sdpFingerprint ? {
        peerId,
        sdpFingerprint,
        verified: false,
        lastChecked: 0,
      } : null,
      identityBinding: identityBinding ?? null,
      throughputHistory: [],
    };

    this.monitoredPeers.set(peerId, monitor);

    // ICE candidate analysis
    peerConnection.addEventListener('icecandidate', (event) => {
      if (event.candidate?.candidate) {
        this.analyzeICECandidate(event.candidate.candidate, peerId, expectedPeers);
      }
    });

    // Connection state → reconnect tracking
    let lastState = peerConnection.connectionState;
    peerConnection.addEventListener('connectionstatechange', () => {
      const newState = peerConnection.connectionState;

      if (lastState === 'connected' && (newState === 'disconnected' || newState === 'failed')) {
        monitor.reconnectCount++;
        if (monitor.reconnectCount > this.config.reconnectAlertThreshold) {
          this.createAlert({
            level: ThreatLevel.MEDIUM,
            type: ThreatType.UNSTABLE_CONNECTION,
            title: 'Unstable Connection Detected',
            details: `Connection to peer ${peerId.slice(0, 8)}... dropped ${monitor.reconnectCount} times`,
            recommendation: 'Possible active interference or network manipulation. Consider using relay mode.',
            technicalData: { peerId: peerId.slice(0, 8), reconnectCount: monitor.reconnectCount },
          });
        }
      }

      // Once connected: verify DTLS fingerprint and start candidate-pair polling
      if (newState === 'connected') {
        this.verifyDTLSFingerprint(peerId);
        this.verifyPeerIdentity(peerId);
        this.startCandidatePairPolling(peerId);
      }

      if (newState === 'closed' || newState === 'failed') {
        this.stopCandidatePairPolling(peerId);
      }

      lastState = newState;
    });
  }

  /**
   * Stop monitoring a peer and clean up resources.
   * Called automatically on connection close/fail, or manually.
   */
  unmonitorPeer(peerId: string): void {
    const existing = this.monitoredPeers.get(peerId);
    if (existing) {
      if (existing.candidatePairPollTimer) clearInterval(existing.candidatePairPollTimer);
      this.monitoredPeers.delete(peerId);
    }
  }

  // ─── Layer 3: ICE Candidate Analysis ────────────────────────────────────

  private analyzeICECandidate(candidate: string, peerId: string, expectedPeers: string[]): void {
    const monitor = this.monitoredPeers.get(peerId);
    if (!monitor) return;

    // Unauthorized TURN/STUN
    if (candidate.includes('turn:') || candidate.includes('turns:')) {
      const isAuthorized = this.config.authorizedServers.some(server => candidate.includes(server));
      if (!isAuthorized) {
        this.createAlert({
          level: ThreatLevel.HIGH,
          type: ThreatType.UNAUTHORIZED_TURN,
          title: 'Unauthorized TURN Server',
          details: 'Unexpected TURN server detected in ICE candidate',
          recommendation: 'Possible interception attempt. Your connection may be routed through an untrusted server.',
          technicalData: { candidate: candidate.substring(0, 120), peerId: peerId.slice(0, 8) },
        });
      }
    }

    // Extract IP (v4 or v6)
    const ip = extractIP(candidate);
    if (!ip) return;

    monitor.iceIPs.add(ip);

    // IP collision check across peers
    if (expectedPeers.length > 1) {
      for (const [otherPeerId, otherMonitor] of this.monitoredPeers.entries()) {
        if (otherPeerId !== peerId && otherMonitor.iceIPs.has(ip)) {
          this.createAlert({
            level: ThreatLevel.MEDIUM,
            type: ThreatType.PEER_IP_COLLISION,
            title: 'IP Address Collision',
            details: `Multiple peers resolving to same IP: ${ip}`,
            recommendation: 'Possible proxy, NAT, or surveillance relay. Verify peer identities.',
            technicalData: { ip, peer1: peerId.slice(0, 8), peer2: otherPeerId.slice(0, 8) },
          });
          break;
        }
      }
    }
  }

  // ─── Layer 4: DTLS Fingerprint Verification  [P0] ──────────────────────

  /**
   * Verify that the DTLS certificate fingerprint seen on the live connection
   * matches the fingerprint from the SDP offer/answer.
   *
   * A mismatch means an active MITM on the UDP path — the attacker had to
   * present their own certificate, which won't match the signaled fingerprint.
   */
  private async verifyDTLSFingerprint(peerId: string): Promise<void> {
    const monitor = this.monitoredPeers.get(peerId);
    if (!monitor?.dtlsFingerprint) return;

    try {
      const senders = monitor.connection.getSenders();
      const dtlsTransport = senders[0]?.transport;
      if (!dtlsTransport) return;

      // getRemoteCertificates() returns ArrayBuffer[] of DER-encoded certs
      const remoteCerts = (dtlsTransport as any).getRemoteCertificates?.();
      if (!remoteCerts || remoteCerts.length === 0) return;

      // Compute SHA-256 fingerprint of the first (leaf) certificate
      const hashBuffer = await crypto.subtle.digest('SHA-256', remoteCerts[0]);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const liveFingerprint = 'sha-256 ' + hashArray.map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(':');

      monitor.dtlsFingerprint.liveFingerprint = liveFingerprint;
      monitor.dtlsFingerprint.lastChecked = Date.now();

      // Compare
      const sdpNormalized = monitor.dtlsFingerprint.sdpFingerprint.toUpperCase().replace(/\s+/g, ' ');
      const liveNormalized = liveFingerprint.toUpperCase().replace(/\s+/g, ' ');

      if (sdpNormalized !== liveNormalized) {
        monitor.dtlsFingerprint.verified = false;
        this.createAlert({
          level: ThreatLevel.CRITICAL,
          type: ThreatType.DTLS_FINGERPRINT_MISMATCH,
          title: 'DTLS Certificate Mismatch — Possible MITM',
          details: `The DTLS certificate on the live UDP connection does not match the fingerprint from the signaled SDP.`,
          recommendation: 'Your P2P connection may be intercepted by a man-in-the-middle. Disconnect immediately and verify peer identity out-of-band.',
          technicalData: {
            peerId: peerId.slice(0, 8),
            sdpFingerprint: sdpNormalized.slice(0, 40) + '...',
            liveFingerprint: liveNormalized.slice(0, 40) + '...',
          },
        });
      } else {
        monitor.dtlsFingerprint.verified = true;
      }
    } catch (err) {
      // getRemoteCertificates not available in all browsers — fail open
      console.warn('[SURVEILLANCE] DTLS fingerprint verification unavailable:', err);
    }
  }

  // ─── Layer 5: Peer Identity Binding  [P0] ──────────────────────────────

  /**
   * Verify peer identity by checking that the Moby public-key address
   * is bound to the expected DTLS fingerprint (signed during signaling).
   */
  private async verifyPeerIdentity(peerId: string): Promise<void> {
    const monitor = this.monitoredPeers.get(peerId);
    if (!monitor?.identityBinding) return;
    if (!monitor.dtlsFingerprint?.liveFingerprint) {
      // DTLS verification hasn't run yet — will be checked after verifyDTLSFingerprint
      return;
    }

    const expected = monitor.identityBinding.expectedFingerprint.toUpperCase().replace(/\s+/g, ' ');
    const actual = monitor.dtlsFingerprint.liveFingerprint.toUpperCase().replace(/\s+/g, ' ');

    monitor.identityBinding.actualFingerprint = actual;
    monitor.identityBinding.lastVerified = Date.now();

    if (expected !== actual) {
      monitor.identityBinding.verified = false;
      this.createAlert({
        level: ThreatLevel.CRITICAL,
        type: ThreatType.PEER_IDENTITY_MISMATCH,
        title: 'Peer Identity Mismatch',
        details: `Peer ${peerId.slice(0, 8)}... presented a DTLS certificate that does not match their signed identity binding.`,
        recommendation: 'Someone may be impersonating this peer. Disconnect and verify their identity through another channel.',
        technicalData: {
          peerAddress: peerId.slice(0, 8),
          expectedFingerprint: expected.slice(0, 40) + '...',
          actualFingerprint: actual.slice(0, 40) + '...',
        },
      });
    } else {
      monitor.identityBinding.verified = true;
    }
  }

  // ─── Layer 6: Candidate Pair Monitoring  [P1] ──────────────────────────

  /**
   * Poll the selected ICE candidate pair for mid-session changes.
   * A silent reroute from host→relay could indicate an active network attack.
   */
  private startCandidatePairPolling(peerId: string): void {
    const monitor = this.monitoredPeers.get(peerId);
    if (!monitor) return;

    // Take initial snapshot
    this.snapshotCandidatePair(peerId);

    monitor.candidatePairPollTimer = setInterval(
      () => this.snapshotCandidatePair(peerId),
      this.config.candidatePairPollIntervalMs
    );
  }

  private stopCandidatePairPolling(peerId: string): void {
    const monitor = this.monitoredPeers.get(peerId);
    if (monitor?.candidatePairPollTimer) {
      clearInterval(monitor.candidatePairPollTimer);
      monitor.candidatePairPollTimer = undefined;
    }
  }

  private async snapshotCandidatePair(peerId: string): Promise<void> {
    const monitor = this.monitoredPeers.get(peerId);
    if (!monitor) return;

    try {
      const stats = await monitor.connection.getStats();
      let nominatedPair: any = null;

      stats.forEach((report: any) => {
        if (report.type === 'candidate-pair' && report.nominated) {
          nominatedPair = report;
        }
      });

      if (!nominatedPair) return;

      // Resolve local and remote candidate details
      let localCandidate: any = null;
      let remoteCandidate: any = null;

      stats.forEach((report: any) => {
        if (report.id === nominatedPair.localCandidateId) localCandidate = report;
        if (report.id === nominatedPair.remoteCandidateId) remoteCandidate = report;
      });

      if (!localCandidate || !remoteCandidate) return;

      const snapshot: CandidatePairSnapshot = {
        peerId,
        localType: localCandidate.candidateType || 'unknown',
        remoteType: remoteCandidate.candidateType || 'unknown',
        localIP: localCandidate.address || localCandidate.ip || '',
        remoteIP: remoteCandidate.address || remoteCandidate.ip || '',
        timestamp: Date.now(),
      };

      const prev = monitor.lastCandidatePair;
      monitor.lastCandidatePair = snapshot;

      if (!prev) return; // First snapshot — nothing to compare

      // Detect rerouting: candidate type changed mid-session
      if (prev.localType !== snapshot.localType || prev.remoteType !== snapshot.remoteType) {
        this.createAlert({
          level: ThreatLevel.HIGH,
          type: ThreatType.CANDIDATE_PAIR_REROUTED,
          title: 'ICE Candidate Pair Rerouted',
          details: `Connection path changed from ${prev.localType}/${prev.remoteType} to ${snapshot.localType}/${snapshot.remoteType}`,
          recommendation: 'Your P2P connection was silently rerouted. This may indicate network manipulation.',
          technicalData: {
            peerId: peerId.slice(0, 8),
            before: { local: prev.localType, remote: prev.remoteType, remoteIP: prev.remoteIP },
            after: { local: snapshot.localType, remote: snapshot.remoteType, remoteIP: snapshot.remoteIP },
          },
        });
      }

      // Detect unexpected relay when both peers should be direct
      if (snapshot.localType === 'relay' || snapshot.remoteType === 'relay') {
        // Only alert if we previously had a direct (host/srflx) path
        if (prev.localType !== 'relay' && prev.remoteType !== 'relay') {
          this.createAlert({
            level: ThreatLevel.MEDIUM,
            type: ThreatType.UNEXPECTED_RELAY,
            title: 'Connection Fell Back to Relay',
            details: `Direct P2P path lost — connection now routed through relay server`,
            recommendation: 'Connection was direct but is now relayed. Verify TURN server is authorized.',
            technicalData: { peerId: peerId.slice(0, 8), candidateType: snapshot.remoteType },
          });
        }
      }
    } catch (err) {
      // getStats can fail if connection is closing — non-fatal
    }
  }

  // ─── Layer 7: Mesh Topology Cross-Check  [P1] ──────────────────────────

  /**
   * Submit a mesh topology report for cross-peer IP consistency analysis.
   * Call from the group call coordinator after all mesh links are established.
   *
   * Delegates to worker if available, otherwise runs on main thread.
   */
  analyzeMeshTopology(report: MeshTopologyReport): void {
    if (this.worker) {
      this.postToWorker({ type: 'analyze-mesh-topology', meshReport: report });
    } else {
      // Fallback: main-thread analysis
      const inconsistencies = analyzeMeshTopology(report);
      for (const inc of inconsistencies) {
        this.createAlert({
          level: ThreatLevel.HIGH,
          type: ThreatType.MESH_TOPOLOGY_INCONSISTENCY,
          title: 'Group Mesh Topology Inconsistency',
          details: inc.details,
          recommendation: 'A link in the group call mesh may be intercepted. Verify peer identities.',
          technicalData: { targetPeer: inc.targetPeer.slice(0, 8), ips: inc.ipsReported },
        });
      }
    }
  }

  // ─── Layer 8: SDP Integrity  [P2] ──────────────────────────────────────

  /**
   * Analyze an SDP offer or answer for integrity issues.
   * Call after receiving remote SDP, before applying it.
   *
   * Returns the DTLS fingerprint from the SDP (for use with monitorPeerConnection).
   */
  analyzeRemoteSDP(peerId: string, sdp: string, direction: 'offer' | 'answer'): string | null {
    if (this.worker) {
      this.postToWorker({ type: 'analyze-sdp', peerId, sdp, direction });
    } else {
      const issues = analyzeSDPContent(peerId, sdp, direction);
      for (const issue of issues) {
        this.createAlert({
          level: issue.type === ThreatType.SDP_CODEC_DOWNGRADE ? ThreatLevel.HIGH : ThreatLevel.MEDIUM,
          type: issue.type,
          title: sdpIssueTitleMap[issue.type] || 'SDP Integrity Issue',
          details: issue.details,
          recommendation: 'Review your connection. SDP may have been tampered with by an intermediary.',
          technicalData: issue.technicalData,
        });
      }
    }

    return extractSDPFingerprint(sdp);
  }

  // ─── Layer 9: Throughput Anomaly  [P2] ─────────────────────────────────

  /**
   * Feed a throughput sample for a peer. Call periodically during active transfers.
   * Analysis runs when enough samples are collected.
   */
  recordThroughputSample(peerId: string, bytesSent: number, bytesReceived: number, expectedBandwidth: number): void {
    const monitor = this.monitoredPeers.get(peerId);
    if (!monitor) return;

    const sample: ThroughputSample = { peerId, bytesSent, bytesReceived, timestamp: Date.now() };
    monitor.throughputHistory.push(sample);

    // Keep last 60 samples (~30s at 500ms intervals)
    if (monitor.throughputHistory.length > 60) {
      monitor.throughputHistory = monitor.throughputHistory.slice(-60);
    }

    // Analyze every 10 samples
    if (monitor.throughputHistory.length % 10 === 0 && monitor.throughputHistory.length >= 10) {
      if (this.worker) {
        this.postToWorker({
          type: 'analyze-throughput',
          peerId,
          samples: monitor.throughputHistory.slice(-20),
          expectedBandwidth,
        });
      } else {
        const result = analyzeThroughput(
          monitor.throughputHistory.slice(-20),
          expectedBandwidth,
          this.config.throughputDropThreshold
        );
        if (result.anomaly) {
          this.createAlert({
            level: ThreatLevel.MEDIUM,
            type: ThreatType.THROUGHPUT_ANOMALY,
            title: 'DataChannel Throughput Anomaly',
            details: result.details,
            recommendation: 'Transfer throughput dropped significantly. Possible throttling or interception on the UDP path.',
            technicalData: { peerId: peerId.slice(0, 8), avgRate: result.avgRate },
          });
        }
      }
    }
  }

  // ─── Layer 2: Network Timing ────────────────────────────────────────────

  /**
   * Measure signaling server RTT variance to detect MITM proxies.
   * Uses the existing /api/healthz endpoint.
   */
  async analyzeNetworkTiming(signalingServerUrl: string): Promise<NetworkTimingStats | null> {
    try {
      const measurements: number[] = [];

      for (let i = 0; i < 10; i++) {
        const start = performance.now();
        await fetch(`${signalingServerUrl}/api/healthz`, { method: 'HEAD' }).catch(() => {});
        const end = performance.now();
        measurements.push(end - start);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (this.worker) {
        this.postToWorker({ type: 'analyze-timing', measurements });
        // Worker will emit timing-result → handled in handleWorkerMessage
        return null; // Async via worker — caller should listen for 'timing-analysis' event
      }

      const stats = analyzeTimingMeasurements(measurements, this.config.timingAnomalyThreshold);
      if (stats.suspicious) {
        this.createAlert({
          level: ThreatLevel.MEDIUM,
          type: ThreatType.TIMING_ANOMALY,
          title: 'Network Timing Anomaly',
          details: `High latency variance detected: ${stats.stdDev.toFixed(2)}ms (avg: ${stats.avg.toFixed(2)}ms)`,
          recommendation: 'Unstable or manipulated connection detected. May indicate proxy or MITM attack.',
          technicalData: { measurements, avg: stats.avg.toFixed(2), stdDev: stats.stdDev.toFixed(2) },
        });
      }

      return stats;
    } catch (error) {
      console.error('[SURVEILLANCE] Network timing analysis failed:', error);
      return null;
    }
  }

  // ─── Daemon Integration (future) ────────────────────────────────────────

  /**
   * Check for daemon connectivity and request OS-level scan.
   * (ORPHANED: Daemon integration coming in future sprint)
   */
  async requestDaemonScan(): Promise<{ success: boolean; threats?: any[] }> {
    try {
      const response = await fetch('http://127.0.0.1:9876/scan/graphite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.threats?.length > 0) {
          data.threats.forEach((threat: any) => {
            this.createAlert({
              level: threat.level as ThreatLevel,
              type: threat.type as ThreatType,
              title: threat.title,
              details: threat.details,
              recommendation: threat.recommendation,
              technicalData: threat.data,
            });
          });
        }
        return { success: true, threats: data.threats };
      }
      return { success: false };
    } catch {
      // Daemon not available (expected until implemented)
      return { success: false };
    }
  }

  // ─── Alert Management ──────────────────────────────────────────────────

  private createAlert(alert: Omit<SurveillanceAlert, 'id' | 'timestamp'>): void {
    // Deduplication: suppress if same type+peer within DEDUP_WINDOW
    const dedupKey = `${alert.type}:${alert.technicalData?.peerId || 'global'}`;
    const lastTime = this.recentAlertKeys.get(dedupKey);
    const now = Date.now();
    if (lastTime && now - lastTime < SurveillanceDetectionService.DEDUP_WINDOW_MS) {
      return; // Suppress duplicate
    }
    this.recentAlertKeys.set(dedupKey, now);

    // Prune old dedup entries
    if (this.recentAlertKeys.size > 200) {
      for (const [key, ts] of this.recentAlertKeys) {
        if (now - ts > SurveillanceDetectionService.DEDUP_WINDOW_MS) {
          this.recentAlertKeys.delete(key);
        }
      }
    }

    const fullAlert: SurveillanceAlert = {
      ...alert,
      id: `alert_${now}_${Math.random().toString(36).substring(2, 11)}`,
      timestamp: now,
      dismissed: false,
    };

    this.alerts.set(fullAlert.id, fullAlert);
    this.emit('alert', fullAlert);

    console.warn(`[SURVEILLANCE] ${fullAlert.level.toUpperCase()}: ${fullAlert.title}`, {
      details: fullAlert.details,
      recommendation: fullAlert.recommendation,
    });
  }

  getAlerts(): SurveillanceAlert[] {
    return Array.from(this.alerts.values()).sort((a, b) => b.timestamp - a.timestamp);
  }

  getActiveAlerts(): SurveillanceAlert[] {
    return this.getAlerts().filter(alert => !alert.dismissed);
  }

  dismissAlert(alertId: string): void {
    const alert = this.alerts.get(alertId);
    if (alert) {
      alert.dismissed = true;
      this.emit('alert-dismissed', alert);
    }
  }

  clearAlerts(): void {
    this.alerts.clear();
    this.emit('alerts-cleared');
  }

  // ─── Configuration ─────────────────────────────────────────────────────

  addAuthorizedServer(server: string): void {
    this.config.authorizedServers.push(server);
    this.postToWorker({ type: 'set-config', config: { authorizedServers: this.config.authorizedServers } });
  }

  updateConfig(config: Partial<SurveillanceConfig>): void {
    this.config = { ...this.config, ...config };
    this.postToWorker({ type: 'set-config', config });
  }

  // ─── Status / Statistics ────────────────────────────────────────────────

  isActive(): boolean {
    return this.isMonitoring;
  }

  getMonitoredPeerCount(): number {
    return this.monitoredPeers.size;
  }

  getPeerVerificationStatus(peerId: string): {
    dtlsVerified: boolean | null;
    identityVerified: boolean | null;
    candidatePairType: string | null;
  } {
    const monitor = this.monitoredPeers.get(peerId);
    if (!monitor) return { dtlsVerified: null, identityVerified: null, candidatePairType: null };
    return {
      dtlsVerified: monitor.dtlsFingerprint?.verified ?? null,
      identityVerified: monitor.identityBinding?.verified ?? null,
      candidatePairType: monitor.lastCandidatePair
        ? `${monitor.lastCandidatePair.localType}/${monitor.lastCandidatePair.remoteType}`
        : null,
    };
  }

  getStatistics(): {
    total: number;
    byLevel: Record<ThreatLevel, number>;
    byType: Partial<Record<ThreatType, number>>;
    active: number;
    dismissed: number;
    monitoredPeers: number;
    verifiedPeers: number;
  } {
    const alerts = this.getAlerts();
    const byLevel: Record<ThreatLevel, number> = {
      [ThreatLevel.INFO]: 0,
      [ThreatLevel.LOW]: 0,
      [ThreatLevel.MEDIUM]: 0,
      [ThreatLevel.HIGH]: 0,
      [ThreatLevel.CRITICAL]: 0,
    };
    const byType: Partial<Record<ThreatType, number>> = {};

    alerts.forEach(alert => {
      byLevel[alert.level]++;
      byType[alert.type] = (byType[alert.type] || 0) + 1;
    });

    let verifiedPeers = 0;
    for (const monitor of this.monitoredPeers.values()) {
      if (monitor.dtlsFingerprint?.verified || monitor.identityBinding?.verified) {
        verifiedPeers++;
      }
    }

    return {
      total: alerts.length,
      byLevel,
      byType,
      active: alerts.filter(a => !a.dismissed).length,
      dismissed: alerts.filter(a => a.dismissed).length,
      monitoredPeers: this.monitoredPeers.size,
      verifiedPeers,
    };
  }
}

// ─── SDP issue title lookup ─────────────────────────────────────────────────

const sdpIssueTitleMap: Partial<Record<ThreatType, string>> = {
  [ThreatType.SDP_CODEC_DOWNGRADE]: 'SDP Codec Downgrade Detected',
  [ThreatType.SDP_EXTENSION_INJECTION]: 'Unknown RTP Extension in SDP',
  [ThreatType.SDP_CANDIDATE_INJECTION]: 'Excessive Candidates in SDP',
};

// ─── Lazy singleton (SSR-safe) ──────────────────────────────────────────────

let _instance: SurveillanceDetectionService | null = null;

export function getSurveillanceDetectionService(config?: Partial<SurveillanceConfig>): SurveillanceDetectionService {
  if (!_instance) {
    _instance = new SurveillanceDetectionService(config);
  }
  return _instance;
}
